import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { ArrowDown2, TickCircle } from 'iconsax-reactjs';
import { Button, type ButtonProps } from '../Button/Button';
import { cx } from '../internal/cx';
import type { Align, Placement } from '../internal/useAnchoredPosition';
import { Popover } from '../Popover/Popover';
import styles from './Menu.module.css';

export interface MenuActionItem {
  type?: 'item';
  id: string;
  label: ReactNode;
  /** Plain text for typeahead when `label` is not a string. */
  textValue?: string;
  icon?: ReactNode;
  description?: ReactNode;
  shortcut?: string;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  /** Called when chosen. For navigation items, call your router's navigate() here. */
  onSelect?: () => void;
}

export interface MenuRadioItem {
  type: 'radio';
  id: string;
  label: ReactNode;
  textValue?: string;
  icon?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface MenuSeparator {
  type: 'separator';
  id: string;
}

export interface MenuHeading {
  type: 'heading';
  id: string;
  label: ReactNode;
}

export type MenuItem = MenuActionItem | MenuRadioItem | MenuSeparator | MenuHeading;

export interface MenuTriggerProps {
  ref: Ref<HTMLButtonElement>;
  id: string;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export interface MenuProps {
  items: MenuItem[];
  /** Custom trigger; spread the given props onto a <button> (or Button/IconButton). */
  renderTrigger?: (props: MenuTriggerProps) => ReactNode;
  /** Text of the default trigger button (when renderTrigger is not given). */
  label?: ReactNode;
  triggerProps?: Omit<ButtonProps, 'onClick' | 'onKeyDown' | 'ref' | 'id'>;
  align?: Align;
  placement?: Placement;
  /** Accessible name for the menu. Defaults to being labelled by the trigger. */
  menuLabel?: string;
  className?: string;
  onOpenChange?: (open: boolean) => void;
}

type FocusIntent = 'first' | 'last';

function isActionable(item: MenuItem): item is MenuActionItem | MenuRadioItem {
  return item.type === undefined || item.type === 'item' || item.type === 'radio';
}

/** WAI-ARIA menu button: arrow keys, Home/End, typeahead, Escape and Tab close. */
export function Menu({
  items,
  renderTrigger,
  label,
  triggerProps,
  align = 'start',
  placement = 'bottom',
  menuLabel,
  className,
  onOpenChange,
}: MenuProps) {
  const triggerId = useId();
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [focusIntent, setFocusIntent] = useState<FocusIntent>('first');
  // Trigger element kept in state (a callback ref) so it can be handed to render props.
  const [triggerEl, setTriggerEl] = useState<HTMLButtonElement | null>(null);
  const anchorRef = useMemo(() => ({ current: triggerEl }), [triggerEl]);
  const menuRef = useRef<HTMLDivElement>(null);
  const typeahead = useRef({ text: '', timer: 0 });

  function setOpenState(next: boolean, intent: FocusIntent = 'first') {
    setFocusIntent(intent);
    setOpen(next);
    onOpenChange?.(next);
  }

  function enabledItems(): HTMLElement[] {
    const menu = menuRef.current;
    if (!menu) return [];
    return Array.from(
      menu.querySelectorAll<HTMLElement>('[data-menu-item]:not([aria-disabled="true"])'),
    );
  }

  useEffect(() => {
    if (!open) return;
    const list = enabledItems();
    const target = focusIntent === 'last' ? list[list.length - 1] : list[0];
    (target ?? menuRef.current)?.focus();
  }, [open, focusIntent]);

  useEffect(() => () => window.clearTimeout(typeahead.current.timer), []);

  function moveFocus(delta: 1 | -1 | 'first' | 'last') {
    const list = enabledItems();
    if (list.length === 0) return;
    const index = list.indexOf(document.activeElement as HTMLElement);
    let next: number;
    if (delta === 'first') next = 0;
    else if (delta === 'last') next = list.length - 1;
    else next = index < 0 ? 0 : (index + delta + list.length) % list.length;
    list[next]?.focus();
  }

  function handleTypeahead(char: string) {
    const state = typeahead.current;
    window.clearTimeout(state.timer);
    state.text += char.toLocaleLowerCase();
    state.timer = window.setTimeout(() => {
      state.text = '';
    }, 500);
    const list = enabledItems();
    const start = Math.max(0, list.indexOf(document.activeElement as HTMLElement));
    const ordered = [...list.slice(start + 1), ...list.slice(0, start + 1)];
    const match = ordered.find((el) =>
      (el.dataset.textValue ?? el.textContent ?? '')
        .trim()
        .toLocaleLowerCase()
        .startsWith(state.text),
    );
    match?.focus();
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(-1);
        return;
      case 'Home':
      case 'PageUp':
        event.preventDefault();
        moveFocus('first');
        return;
      case 'End':
      case 'PageDown':
        event.preventDefault();
        moveFocus('last');
        return;
      case 'Tab':
        event.preventDefault();
        setOpenState(false);
        return;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          if (event.key === ' ') return; // Space activates the focused item.
          handleTypeahead(event.key);
        }
    }
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpenState(true, 'first');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpenState(true, 'last');
    }
  }

  function select(item: MenuActionItem | MenuRadioItem) {
    if (item.disabled) return;
    setOpenState(false);
    item.onSelect?.();
  }

  const triggerA11yProps = {
    id: triggerId,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: () => setOpenState(!open, 'first'),
    onKeyDown: onTriggerKeyDown,
  } as const;

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ ...triggerA11yProps, ref: setTriggerEl })
      ) : (
        <Button
          variant="secondary"
          rightIcon={<ArrowDown2 size={16} color="currentColor" />}
          {...triggerProps}
          {...triggerA11yProps}
          ref={setTriggerEl}
        >
          {label}
        </Button>
      )}
      <Popover
        open={open}
        onClose={() => setOpenState(false)}
        anchorRef={anchorRef}
        panelRef={menuRef}
        id={menuId}
        role="menu"
        aria-label={menuLabel}
        aria-labelledby={menuLabel ? undefined : triggerId}
        align={align}
        placement={placement}
        className={cx(styles.menu, className)}
        onKeyDown={onMenuKeyDown}
      >
        {items.map((item) => {
          if (item.type === 'separator') {
            return <div key={item.id} role="separator" className={styles.separator} />;
          }
          if (item.type === 'heading') {
            return (
              <div key={item.id} role="presentation" className={styles.heading}>
                {item.label}
              </div>
            );
          }
          if (!isActionable(item)) return null;
          const isRadio = item.type === 'radio';
          const danger = !isRadio && item.tone === 'danger';
          return (
            <button
              key={item.id}
              type="button"
              role={isRadio ? 'menuitemradio' : 'menuitem'}
              aria-checked={isRadio ? item.checked : undefined}
              aria-disabled={item.disabled || undefined}
              tabIndex={-1}
              data-menu-item=""
              data-text-value={
                item.textValue ?? (typeof item.label === 'string' ? item.label : undefined)
              }
              className={cx(styles.item, danger && styles.danger)}
              onClick={() => select(item)}
            >
              {item.icon && (
                <span className={styles.icon} aria-hidden="true">
                  {item.icon}
                </span>
              )}
              <span className={styles.text}>
                <span className={styles.label}>{item.label}</span>
                {!isRadio && item.description && (
                  <span className={styles.description}>{item.description}</span>
                )}
              </span>
              {!isRadio && item.shortcut && (
                <span className={styles.shortcut} aria-hidden="true">
                  {item.shortcut}
                </span>
              )}
              {isRadio && (
                <span className={styles.check} aria-hidden="true">
                  {item.checked && <TickCircle size={16} color="currentColor" variant="Bold" />}
                </span>
              )}
            </button>
          );
        })}
      </Popover>
    </>
  );
}
